import { useCallback, useEffect, useMemo, useState } from "react";
import { SEEDED_EMPTY_QUERY } from "#app/lib/filters/seeded-empty-query";
import { z } from "zod";
import {
  Box,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  ScrollArea,
  Text,
  useWidgetViewState,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
  Skeleton,
} from "@salestrak/ui";
import { AdvancedFilters } from "#app/components/filters/advanced-filters";
import { ExpandableSearch } from "@salestrak/ui";
import { FilterToggle } from "#app/components/filters/filter-toggle";
import { IssueGroupByChip } from "#app/components/issues/issue-group-by-chip";
import { ISSUE_TABLE_GROUP_BY_OPTIONS } from "#app/components/issues/issue-grouping";
import { AGE_STATUS, AGE_STATUS_CONFIG } from "#app/lib/age-status";
import type { IssueAgeStatus } from "#app/lib/data";
import { countActiveFilters } from "#app/lib/filters/build-where";
import { issueFilterConfig } from "#app/lib/filters/configs/issue-filter-config";
import type { FilterState } from "#app/lib/filters/types";
import { useFilters } from "#app/lib/hooks/use-filters";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import type { WidgetSection } from "#app/widgets/dashboards/widget-sections";
import { parseWidgetFilters, widgetFiltersSchema } from "#app/widgets/dashboards/widget-filters";
import { useWindowEmptyTitle } from "#app/widgets/dashboards/scope/use-window-copy";
import { useDashboardIssueScope } from "#app/widgets/dashboards/scope/dashboard-scope-context";
import { useDebouncedValue } from "#app/lib/hooks/use-debounced-value";
import { useCreateProjectEmptyAction } from "../shared/create-empty-action";
import { IssueTable } from "../shared/issue-table";
import { IssueList, type IssueListBand } from "../shared/issue-list";
import { WidgetHeaderOnlyMe } from "../shared/widget-header-only-me";
import { BAD_AGE_QUERY } from "#app/widgets/dashboards/widget-queries";
import {
  useEntityList,
  useEntityListTotal,
  type EntityListTransientFilters,
} from "./use-entity-list";
import type { Omittable } from "@salestrak/ui";
import { resolveStoredTitle } from "../shared/derived-titles";

/**
 * `entity-list` — the Add-card "Project list": the scoped projects in one of two
 * layouts over ONE data layer. `layout: "table"` (default) is the shared
 * `IssueTable` (real library `DataTable` + `useIssueColumns`), so every cell
 * routes through `#app/components/entity` and the rows stay live (open, change
 * stage, edit assignees, age dialog). `layout: "list"` is the shared `IssueList`
 * — a stack of the same `ProjectListRow` the summary Projects modules render —
 * for a card read as a queue rather than a grid (My Work's Update needed).
 * Both read `useEntityList` (scope + Filters + search fold) for the ONE `where`
 * they page over — the table windows through EVERY project in scope via
 * per-group server queries, the list appends pages per band as it scrolls;
 * neither is a capped chunk. Both group by the same dimensions over the same
 * server aggregation. The header adds the transient Only-Me + advanced-filter
 * controls that narrow THIS card only.
 *
 * `sort: "age"` is the queue order — longest since the last Update first — and
 * it decides the list row's shape: the row leads with the fact it is sorted
 * by, so the follow-up chip takes the leading slot and the deadline the
 * trailing one (`IssueList`'s `ageLeads`). Grouped by age under that sort, the
 * bands read most-owed first (Stale, then Update soon).
 */
export const entityListConfigSchema = z
  .looseObject({
    sort: z.enum(["created", "updated", "name", "age"]).default("created"),
    layout: z.enum(["table", "list"]).default("table"),
    groupBy: z.enum(["none", "stage", "workflow", "age", "issueType"]).default("none"),
    home: z.boolean().default(false),
    showTitleCount: z.boolean().default(false),
    filters: widgetFiltersSchema.nullish(),
  });

export type EntityListConfig = z.infer<typeof entityListConfigSchema>;
type EntityListGroupBy = EntityListConfig["groupBy"];


/** Transient view-state this card's header writes and its body reads (shape over
 *  the kit's opaque `WidgetViewState` bag). Never persisted: the persisted config
 *  query seeds the visible header filter, then header-published state owns the query
 *  predicate for this viewer/session. Config `showArchived`/`dateRange` still fold
 *  from persisted settings — see `EntityListTransientFilters`. */
type EntityListViewState = EntityListTransientFilters & Omittable<{
  filterCount?: number;
  groupBy?: EntityListGroupBy;
}>;

/**
 * The card body's toolbar controls: a transient, per-viewer grouping choice,
 * Only-Me toggle, advanced-filter popover, and search that narrow THIS card only.
 * Grouping is seeded from persisted Settings → Data, then lives in view-state
 * for this viewer/session (never persisted — same contract as the filters).
 */
function EntityListToolbar({
  groupBy,
  onGroupByChange,
  searchValue,
  onSearchChange,
  seedQuery,
  seedQueryKey,
}: Omittable<{
  groupBy: EntityListGroupBy;
  onGroupByChange: (value: EntityListGroupBy) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  seedQuery?: FilterState | null;
  seedQueryKey: string;
}>) {
  return (
    <Box paddingX="md" paddingY="xs">
      <Box direction="row" align="center" justify="between" gap="sm">
        <IssueGroupByChip groupBy={groupBy} onGroupByChange={onGroupByChange} />
        <EntityListToolbarActions
          key={seedQueryKey}
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          seedQuery={seedQuery}
        />
      </Box>
    </Box>
  );
}

/**
 * The card toolbar's own filter controls: a transient, per-viewer Only-Me toggle +
 * advanced-filter popover that narrow THIS card only. The persisted config query
 * seeds the visible advanced filter so the designed narrowing is inspectable; the
 * header never persists edits back, and after it publishes, it owns the query
 * predicate for this viewer/session. Config-level `showArchived`/`dateRange` still
 * fold from persisted settings in `useEntityList`. Both controls push the APPLIED
 * result into the shared `WidgetViewState`, and both fold centrally in
 * `useScopedWhere`.
 */
function EntityListToolbarActions({
  searchValue,
  onSearchChange,
  seedQuery,
}: Omittable<{
  searchValue: string;
  onSearchChange: (value: string) => void;
  seedQuery?: FilterState | null;
}>) {
  const { patch } = useWidgetViewState();
  const issueScope = useDashboardIssueScope();

  const query = useFilters(issueFilterConfig, {
    mode: "advanced",
    advancedState: seedQuery ?? SEEDED_EMPTY_QUERY,
  });
  const activeCount = query.activeFilterCount;

  useEffect(() => {
    // Active filters publish their built `where`; zero active rows clear it (a
    // half-built row shouldn't narrow the list).
    patch({ filterWhere: activeCount > 0 ? query.where : null, filterCount: activeCount });
  }, [query.where, activeCount, patch]);

  // A re-seed remounts this control; the saved query owns the predicate again
  // until the new builder publishes, so the old `where` must not linger.
  useEffect(() => () => patch({ filterWhere: undefined }), [patch]);

  const clearQuery = useCallback(() => {
    query.clearAll();
    query.addBlock();
  }, [query]);

  return (
    <Box direction="row" align="center" gap="2xs">
      {/* Filter cluster in the shared cross-view order: the quick-filter
        * chip, then Only-Me, then search. */}
      <Popover>
        <PopoverTrigger
          render={
            <FilterToggle
              size="xs"
              icon="filter"
              count={activeCount}
              onClear={clearQuery}
              tooltip="Quickly filter your Projects"
              clearTooltip="Clear all filters"
            />
          }
        />
        {/* The builder is an edge-to-edge form; the stock popover viewport
          * insets its content, so the padding is zeroed at the viewport slot. */}
        <PopoverPopup align="end" padding="none" width="builder">
          <Box direction="column" maxHeight={440}>
            <ScrollArea>
              <AdvancedFilters config={issueFilterConfig} filters={query} scope={issueScope} />
            </ScrollArea>
          </Box>
        </PopoverPopup>
      </Popover>
      <WidgetHeaderOnlyMe />
      {/* The bare library search, not `ViewSearch`: a dashboard can hold
        * several of these widgets, so none may claim the `/` hotkey. */}
      <ExpandableSearch
        size="xs"
        value={searchValue}
        onValueChange={onSearchChange}
        tooltip="Search in your view"
      />
    </Box>
  );
}

/** The live age states in the order a queue reads them, most-owed first — the
 *  same order `IssueList` sorts its age bands under the Follow up sort. */
const AGE_QUEUE_ORDER: readonly IssueAgeStatus[] = [
  AGE_STATUS.offTrack,
  AGE_STATUS.atRisk,
  AGE_STATUS.onTrack,
];

/**
 * The age bands an age-grouped list ALWAYS draws, zero or not: the statuses the
 * seed query narrows to (Update needed: Stale, Update soon — On track is not
 * what the card watches, so it is not a band there), or every live status
 * when the seed does not narrow. Read from the query, not hard-coded, so a
 * gallery preset filtered to Stale alone stands one band.
 */
function ageEmptyBands(seedQuery: FilterState | null | undefined): IssueListBand[] {
  const narrowed = new Set<string>();
  for (const block of seedQuery?.blocks ?? []) {
    for (const filter of block.filters) {
      if (filter.field !== "age.status" || filter.operator !== "in") continue;
      if (!Array.isArray(filter.value)) continue;
      for (const value of filter.value) if (typeof value === "string") narrowed.add(value);
    }
  }
  return AGE_QUEUE_ORDER.filter((status) => narrowed.size === 0 || narrowed.has(status)).map(
    (status) => ({ key: status, label: AGE_STATUS_CONFIG[status].label }),
  );
}

function EntityListRender({ w, config }: WidgetRenderContext<EntityListConfig>) {
  // Transient header filters (Only-Me + advanced query) live in the card's shared
  // view-state and fold in `useScopedWhere`; only the grouping is read here.
  const { state, patch } = useWidgetViewState();
  const transient = state as EntityListViewState;
  const groupBy = transient.groupBy ?? config.groupBy;
  const seedQuery = useMemo(
    () => parseWidgetFilters(config.filters).query as FilterState | null | undefined,
    [config.filters],
  );
  const seedQueryKey = useMemo(() => JSON.stringify(seedQuery ?? null), [seedQuery]);
  const [searchValue, setSearchValue] = useState("");
  // Search is a SERVER predicate (`name contains`, folded in the data layer) so
  // it narrows the whole scoped set, not just the loaded window — debounced so
  // typing doesn't fire a query per keystroke.
  const debouncedSearch = useDebouncedValue(searchValue, 300);
  const { where, orderBy } = useEntityList(config.sort, config.filters, debouncedSearch);
  const tableQuery = useMemo(() => ({ where, orderBy }), [where, orderBy]);
  const emptyTitle = useWindowEmptyTitle("No Projects", config.filters);
  // Creating a Project only answers an UNFILTERED empty list. A list narrowed
  // by a saved query (Update needed: Stale / Update soon) or a search is empty
  // because nothing matches, and a new Project would not land in it either.
  const createProject = useCreateProjectEmptyAction("projects_widget");
  const filtered =
    debouncedSearch.length > 0 ||
    (seedQuery != null && countActiveFilters(seedQuery) > 0);
  const emptyAction = filtered ? undefined : createProject;
  const isList = config.layout === "list";
  // An age-grouped list knows its bands before a row loads, so its empty is
  // those bands counting zero — the structure the card keeps once rows land —
  // unless a search emptied it, when the bands would claim a fact about the
  // whole set that only holds for the typed name.
  const emptyBands = useMemo(
    () => (isList && groupBy === "age" && debouncedSearch.length === 0 ? ageEmptyBands(seedQuery) : undefined),
    [isList, groupBy, debouncedSearch, seedQuery],
  );
  const handleGroupByChange = useCallback(
    (nextGroupBy: EntityListGroupBy) => {
      patch({ groupBy: nextGroupBy });
    },
    [patch],
  );

  return (
    <>
      {config.home ? null : (
        <EntityListToolbar
          groupBy={groupBy}
          onGroupByChange={handleGroupByChange}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          seedQuery={seedQuery}
          seedQueryKey={seedQueryKey}
        />
      )}
      {isList ? (
        // A home list has no toolbar to regroup from, so its bands are the
        // seed's design (Update needed: Stale, then Update soon); the home TABLE
        // stays flat because its rows are an inventory, not a queue.
        <IssueList
          query={tableQuery}
          groupBy={groupBy}
          ageLeads={config.sort === "age"}
          // Every band folds, on the card and in the maximized view alike: a
          // reader working Update soon puts Stale away for the moment, and
          // finds it still away after a reload (`useBandFolds`).
          collapsible
          emptyTitle={emptyTitle}
          emptyAction={emptyAction}
          emptyBands={emptyBands}
          emptyNote={emptyBands ? "Nothing is waiting on you." : undefined}
        />
      ) : (
        <IssueTable
          query={tableQuery}
          w={w}
          emptyTitle={emptyTitle}
          emptyAction={emptyAction}
          groupBy={config.home ? "none" : groupBy}
          home={config.home}
        />
      )}
    </>
  );
}

function EntityListTitleCount({ config }: WidgetRenderContext<EntityListConfig>) {
  if (!config.showTitleCount) return null;
  return <EntityListTitleCountValue config={config} />;
}

function EntityListTitleCountValue({ config }: { config: EntityListConfig }) {
  const { total, loading } = useEntityListTotal(config.sort, config.filters);
  // A short bar holds the suffix's width so the title does not jump when the
  // count lands.
  if (loading) return <Skeleton height={10} width={28} />;
  return (
    <Text size="xs" weight="medium" variant="muted">
      · {total.toLocaleString()}
    </Text>
  );
}

const entityListSettings: WidgetSettingsGroup[] = [
  {
    title: "Data",
    fields: [
      {
        key: "layout",
        kind: "select",
        label: "Layout",
        icon: "table",
        options: [
          { value: "table", label: "Table" },
          { value: "list", label: "List" },
        ],
      },
      {
        key: "sort",
        kind: "select",
        label: "Sort by",
        icon: "arrow-down-wide-narrow",
        options: [
          { value: "created", label: "Newest first" },
          { value: "updated", label: "Recently updated" },
          { value: "name", label: "Name (A–Z)" },
          { value: "age", label: "Longest since update" },
        ],
      },
      {
        key: "groupBy",
        kind: "select",
        label: "Group by",
        icon: "layers",
        options: [...ISSUE_TABLE_GROUP_BY_OPTIONS],
      },
    ],
  },
  {
    title: "Filters",
    fields: [{ key: "filters", kind: "issueFilters", label: "Filters" }],
  },
];

export const entityListDef: WidgetModule<EntityListConfig> = {
  type: "entity-list",
  title: "Project list",
  // Every stored title is the user's (no derived family here); an untitled
  // blank-card placement reads as the "Table" card it came from.
  dynamicTitle: (config) => resolveStoredTitle(config, () => false, "Project list", "Table"),
  icon: "list",
  // A table has no meaningful chart view — its home card maximizes straight to
  // the data view (D13, replacing a host-side type special-case).
  detailPresentation: "data",
  // The maximized table opens on the bands the card stands — Update needed
  // folds into Stale / Update soon there, each an accordion over its own paged
  // window, which is where a band can run to hundreds of Projects. `workflow`
  // and `issueType` are no drawer group fields, so those cards open flat.
  drilldownGroupBy: (config) => (config.groupBy === "none" ? undefined : config.groupBy),
  // Full-bleed body: the table's row dividers + selection wash reach the card
  // edge; the DataTable's own `paddingX` keeps cell content inset. The flag is
  // per module, so the list layout restores that same inset itself
  // (`IssueList`) before its rows bleed their hover wash back into it.
  body: "bleed",
  span: { minW: 3, minH: 3, maxW: 12, maxH: 12, defaultW: 6 },
  configSchema: entityListConfigSchema,
  titleAdornment: EntityListTitleCount,
  settings: entityListSettings,
  catalog: [
    {
      key: "entity-list.projects",
      label: "Project list",
      description: "A live, sortable list of the Projects in scope.",
      section: "projects" satisfies WidgetSection,
      tags: ["projects"] satisfies readonly WidgetTag[],
      defaultConfig: { sort: "created" },
      defaultW: 6,
    },
    {
      key: "entity-list.needs-attention",
      label: "Update needed list",
      description: "Projects in scope whose follow up is Update soon or Stale, most owed first.",
      section: "projects" satisfies WidgetSection,
      tags: ["projects"] satisfies readonly WidgetTag[],
      defaultConfig: { sort: "age", groupBy: "age", filters: { query: BAD_AGE_QUERY } },
      locks: ["filters"],
      defaultW: 6,
    },
  ],
  render: EntityListRender,
};
